/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
// Timeline and Backing Track Types for Jam Track Feature

export interface TimelineEntry {
  chordInstance: number;
  startTime: number;
  endTime: number;
  chord: string; // Standard notation: "Dm", "C7", "G/B"
}

export interface ScaleInfo {
  key: string;
  scaleType: string;
  confidence?: number;
}

export interface BackingTrack {
  id: string;
  youTubeVideoId: string;
  youTubeUrl: string;
  title?: string;
  description?: string;
  genre?: string;
  bpm?: number;
  timeline: TimelineEntry[];
  supportedScales: ScaleInfo[];
  detectionConfidence?: number;
  status: string;
  createdAt: string;
  // Admin fields
  pattern?: TimelinePattern;
  startOffsetSeconds?: number;
}

export interface GenerateTimelineRequest {
  youTubeUrl: string;
  key?: string;
  scale?: string;
  genre?: string;
  bpm?: number;
  expectedChords?: string[];
  pattern?: TimelinePattern;
  startOffsetSeconds?: number;
}

export type TimelinePattern = 
  | '1-chord-per-bar'
  | '2-bars-per-chord' 
  | 'fast-changes'
  | 'waltz-pattern'
  | 'blues-12-bar'
  | 'country-ballad'
  | 'rock-progression';

export interface JamTrackState {
  // Selection state
  availableTracks: BackingTrack[];
  selectedTrack: BackingTrack | null;
  isLoadingTracks: boolean;
  
  // Playback state
  isPlaying: boolean;
  currentTime: number;
  currentChord: TimelineEntry | null;
  currentChordNotes: string[];
  
  // UI state
  isPlaybackMode: boolean;
  showTrackPanel: boolean;
  videoPlayerReady: boolean;
}

export interface ChordHighlight {
  chord: string;
  notes: string[];
  startTime: number;
  endTime: number;
  isActive: boolean;
}

// Scale translation mapping for backend API calls
export const SCALE_TRANSLATIONS: Record<string, string> = {
  'Ionian': 'Major',
  'dorian': 'Dorian',
  'phrygian': 'Phrygian', 
  'lydian': 'Lydian',
  'mixolydian': 'Mixolydian',
  'aeolian': 'Minor',
  'locrian': 'Locrian',
  'harmonicminor': 'Harmonic Minor',
  'pentatonicMajor': 'Major Pentatonic',
  'pentatonicMinor': 'Minor Pentatonic'
};

// Reverse mapping for display
export const SCALE_DISPLAY_MAP: Record<string, string> = {
  'Major': 'Ionian (Major)',
  'Minor': 'Aeolian (Minor)',
  'Dorian': 'Dorian',
  'Phrygian': 'Phrygian',
  'Lydian': 'Lydian', 
  'Mixolydian': 'Mixolydian',
  'Locrian': 'Locrian',
  'Harmonic Minor': 'Harmonic Minor',
  'Major Pentatonic': 'Pentatonic Major',
  'Minor Pentatonic': 'Pentatonic Minor'
};